import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Loader2,
  Pause,
  RotateCw,
  XCircle,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/shared";
import { flowService } from "@/lib/services";
import type { FlowRun, FlowRunSummary } from "@/types";

export function FlowRunHistory() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<FlowRunSummary[]>([]);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [loadedRuns, setLoadedRuns] = useState<Map<string, FlowRun>>(new Map());
  const [loadingRunDetail, setLoadingRunDetail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (flowId) {
      loadRuns();
    }
  }, [flowId]);

  const loadRuns = async () => {
    if (!flowId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await flowService.getRuns(flowId);
      setRuns(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load runs");
      console.error("Error loading runs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRerun = async (run: FlowRunSummary) => {
    if (!flowId) return;
    try {
      // Load full run details if not already cached
      let fullRun = loadedRuns.get(run.uuid);
      if (!fullRun) {
        const response = await flowService.getRun(flowId, run.uuid);
        fullRun = response.data;
      }
      await flowService.run(flowId, fullRun.inputPrompt);
      await loadRuns();
    } catch (err) {
      console.error("Error rerunning flow:", err);
      showToast("Failed to rerun flow", "error");
    }
  };

  const handleExport = async (run: FlowRunSummary) => {
    if (!flowId) return;
    try {
      // Load full run details if not already cached
      let fullRun = loadedRuns.get(run.uuid);
      if (!fullRun) {
        const response = await flowService.getRun(flowId, run.uuid);
        fullRun = response.data;
        setLoadedRuns((prev) => new Map(prev).set(run.uuid, fullRun!));
      }
      const data = {
        run_id: fullRun.uuid,
        flow_id: fullRun.flowUuid,
        status: fullRun.status,
        input: fullRun.inputPrompt,
        steps: fullRun.steps,
        error: fullRun.errorMessage,
        total_tokens: fullRun.totalTokens,
        started_at: fullRun.startedAt,
        completed_at: fullRun.completedAt,
        created_at: fullRun.createdAt,
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flow-run-${run.uuid}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting run:", err);
      showToast("Failed to export run", "error");
    }
  };

  const toggleExpanded = async (runId: string) => {
    const isCurrentlyExpanded = expandedRuns.has(runId);
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (isCurrentlyExpanded) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });

    // Lazy-load full run details when expanding
    if (!isCurrentlyExpanded && !loadedRuns.has(runId) && flowId) {
      try {
        setLoadingRunDetail(runId);
        const response = await flowService.getRun(flowId, runId);
        setLoadedRuns((prev) => new Map(prev).set(runId, response.data));
      } catch (err) {
        console.error("Error loading run details:", err);
        showToast("Failed to load run details", "error");
      } finally {
        setLoadingRunDetail(null);
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-green-400" />;
      case "running":
        return <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />;
      case "failed":
        return <AlertCircle className="h-5 w-5 text-red-400" />;
      case "paused":
        return <Pause className="h-5 w-5 text-yellow-400" />;
      case "cancelled":
        return <XCircle className="h-5 w-5 text-content-tertiary" />;
      default:
        return <Clock className="h-5 w-5 text-content-quaternary" />;
    }
  };

  const formatRunStatus = (status: string) => {
    const styles: Record<string, string> = {
      completed: "bg-green-500/20 text-green-400",
      running: "bg-blue-500/20 text-blue-400",
      failed: "bg-red-500/20 text-red-400",
      paused: "bg-yellow-500/20 text-yellow-400",
      cancelled: "bg-surface-tertiary/20 text-content-tertiary",
    };
    return styles[status] || styles.cancelled;
  };

  const formatDuration = (start?: string, end?: string) => {
    if (!start || !end) return "N/A";
    const duration = new Date(end).getTime() - new Date(start).getTime();
    const seconds = Math.floor(duration / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-content-primary">Error loading runs</h3>
        <p className="text-content-tertiary mt-1">{error}</p>
        <button
          onClick={loadRuns}
          className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/flows/${flowId}`)}
            className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-content-primary">Run History</h1>
            <p className="text-content-tertiary mt-1">
              {runs.length} {runs.length === 1 ? "run" : "runs"}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {runs.length > 0 ? (
          <div className="max-w-5xl space-y-3">
            {runs.map((run) => {
              const isExpanded = expandedRuns.has(run.uuid);
              return (
                <div
                  key={run.uuid}
                  className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden"
                >
                  {/* Run Header */}
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <button
                        onClick={() => toggleExpanded(run.uuid)}
                        className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </button>

                      {getStatusIcon(run.status)}

                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="text-content-primary font-medium">
                            {new Date(run.createdAt).toLocaleDateString()}{" "}
                            {new Date(run.createdAt).toLocaleTimeString()}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${formatRunStatus(
                              run.status,
                            )}`}
                          >
                            {run.status}
                          </span>
                        </div>
                        <div className="text-sm text-content-tertiary mt-1">
                          {run.totalTokens.toLocaleString()} tokens • {formatDuration(run.startedAt, run.completedAt)}
                        </div>
                        {run.status === "failed" && run.errorMessage && (
                          <div className="text-sm text-red-400 mt-1 line-clamp-1">{run.errorMessage}</div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRerun(run)}
                        className="p-2 text-content-tertiary hover:text-brand-primary hover:bg-interactive-hover rounded-lg transition-colors"
                        title="Re-run"
                      >
                        <RotateCw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleExport(run)}
                        className="p-2 text-content-tertiary hover:text-brand-primary hover:bg-interactive-hover rounded-lg transition-colors"
                        title="Export"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <Link
                        to={`/flows/${flowId}/runs/${run.uuid}`}
                        className="px-3 py-1.5 bg-brand-primary text-white text-sm rounded-lg hover:opacity-90 transition-opacity"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-border-primary p-4 bg-surface-base/50">
                      {(() => {
                        if (loadingRunDetail === run.uuid) {
                          return (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="h-5 w-5 text-brand-primary animate-spin" />
                              <span className="ml-2 text-sm text-content-tertiary">Loading run details...</span>
                            </div>
                          );
                        }
                        if (loadedRuns.has(run.uuid)) {
                          const fullRun = loadedRuns.get(run.uuid)!;
                          return (
                            <div className="space-y-4">
                              {/* Input */}
                              <div>
                                <div className="text-xs font-medium text-content-tertiary mb-1">INPUT</div>
                                <div className="text-sm text-content-primary bg-surface-primary p-3 rounded-lg border border-border-primary">
                                  {fullRun.inputPrompt}
                                </div>
                              </div>

                              {/* Steps */}
                              <div>
                                <div className="text-xs font-medium text-content-tertiary mb-2">
                                  STEPS ({fullRun.steps.length})
                                </div>
                                <div className="space-y-2">
                                  {fullRun.steps.map((step) => (
                                    <div
                                      key={step.id}
                                      className={`p-3 bg-surface-primary rounded-lg border ${
                                        step.status === "failed" ? "border-red-500/30" : "border-border-primary"
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="h-6 w-6 rounded-full bg-surface-secondary flex items-center justify-center text-xs text-content-tertiary">
                                          {step.stepNumber}
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-sm text-content-primary">
                                            {step.label || step.agentName || "Step"}
                                          </div>
                                          <div className="text-xs text-content-tertiary">
                                            {step.tokensUsed.toLocaleString()} tokens
                                          </div>
                                        </div>
                                        {getStatusIcon(step.status)}
                                      </div>
                                      {step.errorMessage && (
                                        <div className="mt-2 ml-9 text-sm text-red-300 bg-red-500/10 p-2 rounded-sm border border-red-500/20">
                                          {step.errorMessage}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {fullRun.errorMessage && (
                                <div>
                                  <div className="text-xs font-medium text-red-400 mb-1">ERROR</div>
                                  <div className="text-sm text-red-300 bg-red-500/10 p-3 rounded-lg border border-red-500/30">
                                    {fullRun.errorMessage}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div className="text-sm text-content-tertiary text-center py-4">
                            Failed to load run details.
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <Zap className="h-16 w-16 text-content-quaternary mb-4" />
            <h3 className="text-lg font-medium text-content-primary">No runs yet</h3>
            <p className="text-content-tertiary mt-1">Run this flow to see execution history here</p>
            <button
              onClick={() => navigate(`/flows/${flowId}/run`)}
              className="mt-6 flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Zap className="h-4 w-4" />
              Run Flow
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
